import { Component, ViewChild } from "@angular/core";
import { ModalController, ToastController } from "@ionic/angular";
import { IUser } from "src/app/interfaces/user.interface";
import { AuthService } from "src/app/services/auth.service";
import { UserAdminService } from "src/app/services/user.service";
import { AgGridAngular } from 'ag-grid-angular';
import { CellClickedEvent, ColDef, SelectionChangedEvent } from 'ag-grid-community';
import * as _ from 'lodash'
import { AdminUserDetailsModal } from "../admin-user-details-modal/admin-user-details-modal";
import { getCreatedDate, fmtDate } from "src/app/util/util";

const fmtIsoDate = d => fmtDate(d, 'YYYY-MM-DD hh:mma')

interface Table { cols: ColDef[], defColDef: ColDef }
const userTable: Table = {
	cols:  [
		{ field: 'name', headerName: 'Name', valueGetter: (params) => `${params.data.firstname} ${params.data.lastname}`, checkboxSelection: true, headerCheckboxSelection: true, flex: 1 },
		{ field: 'local.email', headerName: 'Email', flex: 1 },
		{ field: 'createdAt', headerName: 'Account Created', valueFormatter: (p) => fmtIsoDate(p.data.createdAt) },
	],
	defColDef: {
		sortable: true,
		filter: true,
		resizable: true,
	},
}

@Component({
  selector: "admin-user-mgt",
  templateUrl: "./admin-user-mgt.html",
  styleUrls: ["./admin-user-mgt.scss"],
})
export class AdminUserMgtPage {
	users: (IUser & { createdAt: Date })[];

  // grid props
  cols: ColDef[] = userTable.cols;
  defColDef: ColDef = userTable.defColDef;
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  selected: IUser[] = [];
  loading = false;
  get displayedRows() {
    return this.agGrid.api.getDisplayedRowCount() || 0;
  }

  view: "users" | "admins" = "users";
	search: string 

  constructor(
    public userService: UserAdminService,
    public auth: AuthService,
    public toastCtl: ToastController,
    public modalCtl: ModalController
  ) {}

  /**
   * @Note onInit is not called because onGridReady is invoked when AG Table loads
   */

  // ------------------------------------ Button Events
  async load() {
    this.loading = true;
    try {
      const unformattedUsers = await this.userService.adminFindAll();
      console.log("Users: ", this.users);
      // append user creation date from by deriving it from the ID
      this.users = unformattedUsers
        .map((u) => ({ ...u, createdAt: getCreatedDate(u._id) }))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (e) {
      console.error(`Error loading users`, e);
      const toast = await this.toastCtl.create({
        message: "Error loading users",
				duration: 3000
      });
      await toast.present();
    }
    this.loading = false;
  }

  async deleteUsers() {
    const msg =
      `Are you sure you'd like to delete ${this.selected.length} users?\n` +
      this.selected
        .map((u) => `  - ${u.firstname} ${u.lastname} (${u.local.email})?`)
        .join("\n");
    if (confirm(msg)) {
      this.loading = true;
      // delete users in parallel, display a toast for any that error
      const deletedUsers = await Promise.all(
        this.selected.map((u) => {
          const res = this.userService
            .delete(u._id)
            .toPromise()
            .catch(async (e) => {
              console.error(`Error deleting user: `, u, e);
              const toast = await this.toastCtl.create({
                message: `Error deleting user ${u.firstname} ${u.lastname} (${u.local.email})`,
                position: "top",
								duration: 3000
              });
              await toast.present();
              return null;
            });
          return res;
        })
      );
      const successfullyDeletedCount = deletedUsers.filter((u) => u);
      const toast = await this.toastCtl.create({
        position: "top",
        message: `Successfully deleted ${successfullyDeletedCount.length} / ${this.selected.length}`,
				duration: 3000
      });
      await toast.present();
      this.selected = [];
      this.loading = false;
      await this.load();
    }
  }

  // ------------------------------------ Grid Methods
  async onGridReady() {
    await this.load();
    const me = this.auth.getUser();
    this.agGrid.gridOptions.isRowSelectable = (node) =>
      node.data._id !== me._id;
		// we need a second delay to allow the filtered view to set
		setTimeout(() => {
			this.toggleView(this.view);
		}, 1);
  }

  async onSelectionChanged(params: SelectionChangedEvent) {
    const selected = params.api.getSelectedNodes();
    this.selected = selected.map((row) => row.data);
  }

  async onCellClicked(e: CellClickedEvent) {
    const modal = await this.modalCtl.create({
      component: AdminUserDetailsModal,
      componentProps: { user: e.data },
    });
    await modal.present();
  }

  // ------------------------------------ Other
  filterUsers() {
		this.agGrid.api.setRowData(this.users.filter(u => {
			const cols = [u.firstname, u.lastname, fmtIsoDate(u.createdAt)].map(c => c.toLowerCase())
			const matchesSearch = !this.search || (
				this.search && cols.some(c => c.includes(this.search))
			)
			const matchesUserType = this.view === 'users' ? !u.roles.includes('admin') : u.roles.includes('admin')
			return matchesSearch && matchesUserType
		}))
	}

	searchUsers(event) {
		this.search = event.detail.value.toLowerCase()
		this.filterUsers()
	}

  toggleView(view: "users" | "admins") {
    this.view = view;
		this.filterUsers()
  }
}